import React from 'react';
import type { SortDescriptor } from '@react-types/shared';
import { createSortDescriptor } from '@/components/table';
import { OrganizationItem } from '@/types';

export const useOrganizationsSorting = (organizations: OrganizationItem[]) => {
  const [sortDescriptor, setSortDescriptor] = React.useState<SortDescriptor | undefined>(undefined);

  const sortedItems = React.useMemo(() => {
    if (!organizations.length) return [];
    if (!sortDescriptor?.column) return organizations;

    const copy = [...organizations];
    const { column, direction } = sortDescriptor;

    const compare = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
    const compareDates = (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime();

    copy.sort((a, b) => {
      let cmp = 0;

      switch (column) {
        case 'name':
          cmp = compare(a.name, b.name);
          break;
        case 'status':
          cmp = compare(a.status, b.status);
          break;
        case 'user': {
          const formatOwnerName = (user: OrganizationItem['user']) => {
            if (!user) return '';
            return `${user.first_name} ${user.last_name}`.trim() || user.email;
          };
          cmp = compare(formatOwnerName(a.user), formatOwnerName(b.user));
          break;
        }
        case 'created_at':
          cmp = compareDates(a.created_at, b.created_at);
          break;
        default:
          cmp = 0;
      }

      return direction === 'descending' ? -cmp : cmp;
    });

    return copy;
  }, [organizations, sortDescriptor]);

  return { sortDescriptor, setSortDescriptor, sortedItems };
};